import React from 'react';
import * as R from 'ramda';
import {
  Autocomplete,
  Chip,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { useUserRolePermissions } from 'hooks';
import { func, shape } from 'prop-types';
import { renderInput } from '../utils/renderUtils';
import { useGetCarriersQuery } from '../customerAuthorizationsEndpoints';

export const renderTags = (tags, getTagProps) =>
  tags.map((item, index) => (
    <Chip
      label={item.carrierName}
      sx={{ borderColor: 'secondary.main', borderRadius: 25 }}
      variant="outlined"
      {...getTagProps({ index })}
    />
  ));

const CarrierSelection = ({ info, setInfo }) => {
  const { carriers, isCarrierRestricted, terminals = [] } = info;
  const { currentAffiliate } = useUserRolePermissions();

  const params = {
    supplier: {
      companyCustomerIdName: currentAffiliate?.customerIdName,
      dataCompanyId: currentAffiliate?.dataCompanyId,
    },
    terminals: terminals.map(R.prop('terminalId')),
  };

  const shouldSkip = R.or(
    R.equals('false', isCarrierRestricted?.toString()),
    R.and(R.isEmpty(info), R.isEmpty(terminals))
  );

  const { data: carriersQuery = [], isLoading } = useGetCarriersQuery(
    {
      params,
    },
    {
      skip: shouldSkip,
    }
  );

  const carrierLength = R.length(carriers);
  const displayCounter = carrierLength > 0;

  const handleCarrierSelection = R.compose(
    setInfo,
    R.assoc('carriers'),
    R.flatten,
    R.of,
    R.nthArg(1)
  );

  return (
    <>
      <Stack direction="column" spacing={1} sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1}>
          <Typography variant="subtitle1">Carriers</Typography>
          {displayCounter > 0 && <Chip label={carrierLength} type="accordion" variant="badge" />}
        </Stack>
        <Typography variant="caption">
          By selecting a carrier, you are customizing their permission to load at a terminal.
        </Typography>
      </Stack>
      <RadioGroup
        aria-labelledby="carrier-group"
        name="carrier-selection"
        onChange={e => {
          const value = R.path(['target', 'value'], e);
          if (R.equals('false', value)) {
            setInfo(
              R.evolve(
                {
                  carriers: R.always([]),
                  isCarrierRestricted: R.always(value),
                },
                info
              )
            );
          } else {
            setInfo(R.assoc('isCarrierRestricted', value, info));
          }
        }}
        sx={{ mb: 2 }}
        value={R.defaultTo('false', isCarrierRestricted?.toString())}
      >
        <FormControlLabel
          control={<Radio />}
          label={
            <span>
              <b>Allow all</b> carriers to load
            </span>
          }
          value="false"
        />
        <FormControlLabel
          control={<Radio />}
          label={
            <span>
              <b>Allow selected</b> carriers to load
            </span>
          }
          value="true"
        />
        {/* <FormControlLabel
            control={<Radio />}
            label="Do not allow selected carriers to load"
            value="not"
          /> */}
      </RadioGroup>
      {isCarrierRestricted?.toString() === 'true' && (
        <Autocomplete
          getOptionLabel={R.prop('carrierName')}
          isOptionEqualToValue={R.eqProps('carrierId')}
          loading={isLoading}
          multiple
          onChange={handleCarrierSelection}
          options={carriersQuery}
          renderInput={renderInput({
            label: 'Search Carriers',
            length: carrierLength,
            loading: isLoading,
          })}
          renderTags={renderTags}
          value={R.defaultTo([], carriers)}
        />
      )}
    </>
  );
};

CarrierSelection.propTypes = {
  info: shape({}),
  setInfo: func.isRequired,
};

CarrierSelection.defaultProps = {
  info: {},
};

export default CarrierSelection;
